import React, { memo, useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { monthCells } from './calendarModel';

export const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// Bir ayın ızgarası. Ölçüler onaylı taslaktan (docs/superpowers/specs/2026-10-30-takvimim-taslak-web.html):
// satır 28 px, tek günlük işaret ≤ 24 px tam daire, şerit hafta satırı kırılımında yuvarlak uçla biter.
function MonthGrid({
  year, monthIndex, index, visible, today, isCurrentMonth, pickedDate, onDayEnter, onDayActivate, dayLabel,
}) {
  const { t } = useTranslation();
  const cells = useMemo(
    () => monthCells(year, monthIndex, index, visible),
    [year, monthIndex, index, visible],
  );

  return (
    <Month $current={isCurrentMonth} data-testid="calendar-month">
      <MonthHead>
        <MonthName $current={isCurrentMonth}>{t(`months.${monthIndex}`)}</MonthName>
        {isCurrentMonth && <ThisMonth>{t('myCalendar.thisMonth')}</ThisMonth>}
      </MonthHead>
      <Weekdays aria-hidden="true">
        {WEEKDAY_KEYS.map((k, i) => (
          <span key={k} className={i >= 5 ? 'we' : undefined}>{t(`myCalendar.weekdaysShort.${k}`)}</span>
        ))}
      </Weekdays>
      <Grid>
        {cells.map((c, i) => {
          if (!c) return <span key={`e${i}`} className="day empty" />;
          const { cell } = c;
          const clickable = !!(cell.kind || cell.hourlyDot);
          const cls = ['day'];
          if (c.isWeekend) cls.push('we');
          if (cell.kind) cls.push(`k-${cell.kind}`);
          if (cell.soft) cls.push('soft');
          if (cell.half) cls.push('half');
          if (cell.hourlyDot) cls.push(`has-p${cell.hourlyDot}`);
          if (c.date === today) cls.push('today');
          if (clickable) cls.push('clickable');
          if (pickedDate === c.date) cls.push('picked');
          const band = ['band', cell.kind];
          if (!c.joinLeft) band.push('rl');
          if (!c.joinRight) band.push('rr');
          if (cell.soft) band.push('soft');
          if (cell.half) band.push('half');

          return (
            <div
              key={c.date}
              className={cls.join(' ')}
              data-date={c.date}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              aria-label={clickable ? dayLabel(c) : undefined}
              onMouseEnter={(e) => onDayEnter(c, e.currentTarget)}
              onClick={clickable ? (e) => onDayActivate(c, e.currentTarget) : undefined}
              onKeyDown={clickable ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onDayActivate(c, e.currentTarget);
                }
              } : undefined}
            >
              <span className="hov" />
              {cell.kind && <span className={band.join(' ')} data-testid="day-band" />}
              {c.date === today && <span className="ring" />}
              <span className="num">{c.day}</span>
              {cell.holidayDot && <span className="hdot" />}
              {cell.hourlyDot && <span className={`pdot ${cell.hourlyDot}`} />}
            </div>
          );
        })}
      </Grid>
    </Month>
  );
}

export default memo(MonthGrid);

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const pulse = (c, glow) => keyframes`
  0%, 100% { box-shadow: 0 0 0 2px ${c}; }
  50% { box-shadow: 0 0 0 7px ${glow}, 0 0 0 2px ${c}; }
`;

const Month = styled.div`
  padding: 10px 6px 6px;
  border-radius: 12px;
  transition: background 0.2s;
  ${({ $current, theme }) => $current && css`
    background: ${theme.colors.calendar.currentMonthBg};
    box-shadow: inset 0 0 0 1px ${theme.colors.calendar.currentMonthBorder};
  `}
`;

const MonthHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 0 4px 8px;
`;

const MonthName = styled.span`
  font-size: 15px;
  font-weight: 800;
  color: ${({ $current, theme }) => ($current ? theme.colors.primary.main : theme.colors.text.primary)};
`;

const ThisMonth = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary.main};
  background: ${({ theme }) => theme.colors.calendar.approvedTagBg};
  padding: 2px 8px;
  border-radius: 999px;
`;

const Weekdays = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);

  span {
    text-align: center;
    font-size: 11px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.calendar.faint};
    padding-bottom: 4px;
  }
  span.we { color: ${({ theme }) => theme.colors.calendar.weekendFaint}; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 3px;

  ${({ theme }) => {
    const c = theme.colors.calendar;
    const white = theme.colors.text.light.very;
    return css`
      .day { position: relative; height: 28px; display: flex; align-items: center; justify-content: center; outline: none; }
      .day.empty { pointer-events: none; }
      .day.clickable { cursor: pointer; }
      .day.clickable:focus-visible .hov { opacity: 1; box-shadow: 0 0 0 2px ${theme.colors.primary.main}; }
      .num { position: relative; z-index: 2; font-size: 12.5px; font-weight: 600; font-variant-numeric: tabular-nums; line-height: 1; color: ${theme.colors.text.primary}; }
      .day.we .num { color: ${c.faint}; }
      .hov { position: absolute; inset: 2px 3px; border-radius: 10px; background: ${c.cellHover}; opacity: 0; transition: opacity 0.12s; z-index: 0; }
      .day:not(.empty):hover .hov { opacity: 1; }
      .day.picked .hov { opacity: 1; background: ${c.cellPicked}; }

      .band { position: absolute; top: 2px; bottom: 2px; left: 0; right: 0; z-index: 1; animation: ${fadeIn} 0.18s ease; }
      .band.rl { left: 2px; border-top-left-radius: 12px; border-bottom-left-radius: 12px; }
      .band.rr { right: 2px; border-top-right-radius: 12px; border-bottom-right-radius: 12px; }
      .band.rl.rr {
        left: 50%; top: 50%; right: auto; bottom: auto; width: min(24px, calc(100% - 2px)); aspect-ratio: 1;
        transform: translate(-50%, -50%); border-radius: 50%;
      }

      .band.holiday { background: ${c.holidayBg}; }
      .day.k-holiday .num { color: ${c.holidayText}; font-weight: 800; }
      .band.holiday.half { background: linear-gradient(135deg, ${white} 0 50%, ${c.holidayHalfFill} 50% 100%); box-shadow: inset 0 0 0 1.5px ${c.holidayHalfBorder}; }

      .band.approved { background: ${c.approved}; }
      .day.k-approved .num { color: ${white}; font-weight: 700; }
      .band.approved.soft { background: ${c.approvedSoft}; }
      .day.k-approved.soft .num { color: ${c.approvedSoftText}; font-weight: 600; }
      .band.approved.half { background: linear-gradient(135deg, ${c.approvedSoft} 0 50%, ${c.approved} 50% 100%); }
      .day.k-approved.half .num { color: ${c.approvedSoftText}; }

      .band.waiting { background: ${c.waitingBg}; border-top: 1.5px dashed ${c.waitingBorder}; border-bottom: 1.5px dashed ${c.waitingBorder}; }
      .band.waiting.rl { border-left: 1.5px dashed ${c.waitingBorder}; }
      .band.waiting.rr { border-right: 1.5px dashed ${c.waitingBorder}; }
      .band.waiting.rl.rr { border: 1.5px dashed ${c.waitingBorder}; }
      .band.waiting.soft { background: ${c.waitingSoftBg}; }
      .band.waiting.half { background: linear-gradient(135deg, ${c.waitingSoftBg} 0 50%, ${c.waitingBg} 50% 100%); }
      .day.k-waiting .num { color: ${c.waitingText}; font-weight: 700; }
      .day.k-waiting.soft .num { font-weight: 600; opacity: 0.75; }

      .hdot { position: absolute; z-index: 3; top: 3px; right: 5px; width: 6px; height: 6px; border-radius: 50%; background: ${c.holidayText}; box-shadow: 0 0 0 1.5px ${white}; }
      .pdot { position: absolute; z-index: 3; bottom: 3px; left: 50%; width: 5px; height: 5px; margin-left: -2.5px; border-radius: 50%; }
      .pdot.approved { background: ${c.approved}; }
      .pdot.waiting { background: ${c.waitingBorder}; }

      .day.today .ring {
        position: absolute; z-index: 2; width: min(24px, calc(100% - 2px)); aspect-ratio: 1; left: 50%; top: 50%;
        transform: translate(-50%, -50%); border-radius: 50%; box-shadow: 0 0 0 2px ${theme.colors.primary.main};
      }
      .day.today .num { color: ${theme.colors.primary.main}; font-weight: 800; }
      .day.today.k-approved:not(.soft) .ring { box-shadow: 0 0 0 2px ${white}; }
      .day.today.k-approved:not(.soft) .num { color: ${white}; }
      @media (prefers-reduced-motion: no-preference) {
        .day.flash .ring { animation: ${pulse(theme.colors.primary.main, c.todayPulse)} 1.2s ease 2; }
      }

      .band, .num { transition: opacity 0.15s; }
    `;
  }}
`;
